<template>
    <teleport to="body" v-if="isOpen">
        <!-- Overlay full-screen -->
        <div
            class="fixed inset-0 w-screen h-screen flex items-center justify-center"
            :style="{ zIndex: zIndex, background: 'rgba(30, 41, 59, 0.50)' }"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ineligibility-title"
            @click="emitClose"
        >
            <!-- Dialog -->
            <div
                class="relative bg-white dark:bg-slate-800 w-screen h-screen rounded-none flex flex-col md:w-[640px] md:max-w-[95vw] md:h-auto md:max-h-[80vh] md:rounded-lg shadow-[0_1px_3px_0_rgba(0,0,0,0.10),0_1px_2px_0_rgba(0,0,0,0.06)]"
                @click.stop
            >
                <!-- Header -->
                <div
                    class="flex items-center justify-between px-6 pt-5 pb-3 w-full"
                >
                    <h3
                        id="ineligibility-title"
                        class="text-left text-slate-800 dark:text-slate-100 text-[20px] font-medium leading-[28px]"
                    >
                        You're likely ineligible for this card
                    </h3>
                    <button
                        type="button"
                        class="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700 cursor-pointer focus:outline-none"
                        @click="emitClose"
                        aria-label="Close"
                    >
                        <Icon icon="x" class="w-5 h-5 text-slate-500 dark:text-slate-400" />
                        <span class="sr-only">Close</span>
                    </button>
                </div>

                <!-- Content -->
                <div class="px-6 pb-28 md:pb-3 flex-1 overflow-y-auto">
                    <div class="flex w-full p-6 flex-col items-center gap-8">
                        <!-- Image block (responsive, background cover, aspect ratio) -->
                        <div
                            class="w-full max-w-[268px] mx-auto rounded-xl bg-gray-200 shrink-0"
                            :style="{
                                aspectRatio: '268 / 166.61',
                                backgroundImage: cardImageUrl
                                    ? `url(${cardImageUrl})`
                                    : undefined,
                                backgroundPosition: '50% 50%',
                                backgroundSize: 'cover',
                                backgroundRepeat: 'no-repeat',
                            }"
                        ></div>

                        <!-- Description -->
                        <div class="flex flex-col items-start gap-4 w-full">
                            <p class="text-slate-800 dark:text-slate-100 text-sm leading-5">
                                Unfortunately you're likely ineligible for the
                                <span class="font-medium text-slate-800 dark:text-slate-100">
                                    {{ card?.title }}
                                </span>
                                for the following reasons:
                            </p>
                        </div>

                        <!-- Reasons list -->
                        <div class="w-full">
                            <ul class="flex flex-col items-start w-full">
                                <li
                                    v-for="(ex, idx) in exclusions"
                                    :key="`ex-${idx}`"
                                    class="flex items-start gap-3 w-full mb-4"
                                >
                                    <Icon
                                        icon="info"
                                        class="w-5 h-5 text-slate-800 dark:text-slate-100 mt-0.5"
                                    />
                                    <div
                                        class="flex flex-col items-start flex-1 w-full"
                                    >
                                        <p
                                            class="text-slate-800 dark:text-slate-100 text-sm font-medium leading-5"
                                            v-if="ex.title"
                                        >
                                            {{ ex.title }}
                                        </p>
                                        <div class="modal-exclusion w-full">
                                            <ExclusionText
                                                :text="ex.text"
                                                :hasTooltip="ex.hasTooltip"
                                            />
                                        </div>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <!-- Mobile fixed CTA -->
                <div
                    class="md:hidden fixed left-0 right-0 bottom-0 p-4 bg-white dark:bg-slate-800"
                    style="
                        padding-bottom: calc(
                            16px + env(safe-area-inset-bottom)
                        );
                        box-shadow: 0 -1px 2px rgba(0, 0, 0, 0.06);
                    "
                >
                    <a
                        v-if="showApplyButton"
                        :href="card.application_link"
                        target="_blank"
                        class="w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg bg-orange text-white text-sm font-medium hover:bg-orange-600"
                    >
                        Learn how to apply
                        <Icon
                            icon="arrow-up-right"
                            class="w-4 h-4 text-white"
                        />
                    </a>
                    <a
                        v-else-if="showApplyLink"
                        :href="card.application_link"
                        target="_blank"
                        class="w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg text-orange hover:text-orange-600 text-sm font-medium"
                    >
                        Learn how to apply
                        <Icon icon="arrow-up-right" class="w-4 h-4" />
                    </a>
                    <button
                        v-else
                        type="button"
                        class="w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium"
                        @click="emitClose"
                    >
                        Close
                    </button>
                </div>

                <!-- Footer (desktop only) -->
                <div
                    class="hidden md:flex items-center justify-end gap-2 px-6 py-3 w-full"
                >
                    <button
                        type="button"
                        class="inline-flex items-center gap-2 px-4 py-2.5 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm text-slate-800 dark:text-slate-100 text-sm font-medium"
                        @click="emitClose"
                    >
                        Close
                    </button>
                    <a
                        v-if="showApplyButton"
                        :href="card.application_link"
                        target="_blank"
                        class="inline-flex items-center gap-2 px-4 py-2.5 rounded bg-orange text-white text-sm font-medium hover:bg-orange-600"
                    >
                        Learn how to apply
                        <Icon
                            icon="arrow-up-right"
                            class="w-4 h-4 text-white"
                        />
                    </a>
                    <a
                        v-else-if="showApplyLink"
                        :href="card.application_link"
                        target="_blank"
                        class="inline-flex items-center gap-2 px-4 py-2.5 rounded text-orange hover:text-orange-600 text-sm font-medium"
                    >
                        Learn how to apply
                        <Icon icon="arrow-up-right" class="w-4 h-4" />
                    </a>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script>
import { mapState } from 'pinia';
import { useNextCardStore } from '@/store/nextcard';
import ExclusionText from '@/components/next-card/exclusion-text.vue';
import Icon from '@/components/ui/icon/icon.vue';

export default {
    name: 'IneligibilityModal',
    emits: ['close'],
    components: {
        ExclusionText,
        Icon,
    },
    props: {
        isOpen: {
            type: Boolean,
            default: false,
        },
        card: {
            type: Object,
            required: true,
        },
        offenders: {
            type: Object,
            default: () => ({}),
        },
        zIndex: {
            type: Number,
            default: 200000,
        },
    },
    computed: {
        ...mapState(useNextCardStore, ['activeUser']),
        cdnUrl() {
            return window.cdnUrl;
        },
        showApplyButton() {
            return (
                !!this.card?.application_link &&
                this.card?.issuing_bank?.link_button_visible === true
            );
        },
        showApplyLink() {
            return (
                !!this.card?.application_link &&
                this.card?.issuing_bank?.link_button_visible === false
            );
        },
        cardImageUrl() {
            const p = this.card?.card_image?.path;
            const f = this.card?.card_image?.file_name;
            if (!p || !f) return null;
            return `${this.cdnUrl}/${p}/_small/${f}`;
        },
        hasSet() {
            return this.card?.omitted_results
                ? this.card.omitted_results.hasOwnProperty('sets')
                : false;
        },
        exclusions() {
            let exclusions = [];
            let uniques = [];
            if (this.hasSet) {
                this.card.omitted_results.sets?.forEach((set) => {
                    set.reasons?.forEach((reason) => {
                        uniques.push(reason);
                    });
                });
                let uniqueItems = [...new Set(uniques)];
                uniqueItems.forEach((unq) => {
                    exclusions.push(this.setExclusion(unq));
                });
                return exclusions;
            } else {
                const list = Array.isArray(this.card?.omitted_results)
                    ? this.card.omitted_results
                    : [];
                // De-dup family conflict pair
                const hasCurrent = list.includes('current_card_holder');
                const hasFamily = list.includes('current_card_holder_family');
                const filtered =
                    hasCurrent && hasFamily
                        ? list.filter((x) => x !== 'current_card_holder_family')
                        : list;
                filtered.forEach((el) =>
                    exclusions.push(this.setExclusion(el))
                );
            }
            return exclusions;
        },
    },
    methods: {
        emitClose() {
            this.$emit('close');
        },
        setExclusion(ex) {
            let obj = {
                title: '',
                type: '',
                text: '',
                offenders: [],
                hasTooltip: false,
            };
            let options = '';
            let extraData = '';
            switch (ex) {
                case 'five_twenty_four':
                    obj.title = 'Over 5/24';
                    obj.type = '';
                    obj.text =
                        "This card isn't available since you’re above 5/24.";
                    obj.text +=
                        ' <span class="text-sm text-slate-500 dark:text-slate-400 block">(Currently ' +
                        (this.activeUser?.omitted_cards
                            ?.five_twenty_four_count || 0) +
                        '/24)</span>';
                    obj.offenders = [];
                    break;
                case 'current_card_holder':
                    obj.title = 'Current Cardholder';
                    obj.type = '';
                    obj.text =
                        "You can't open a new version of this card while currently being a cardholder.";
                    if (this.offenders.hasOwnProperty('current_card_holder')) {
                        this.offenders['current_card_holder'].forEach(
                            (item) => {
                                obj.text +=
                                    '<div class="text-sm text-slate-500 dark:text-slate-400 block">(' +
                                    item.card_title +
                                    (item.last_four
                                        ? ' <span class="text-xs">- ' +
                                          item.last_four +
                                          '</span>'
                                        : '') +
                                    ')</div>';
                            }
                        );
                    }
                    obj.offenders = [];
                    break;
                case 'cardholder_another_card':
                    obj.title = 'Cardholder of Another Card';
                    obj.type = '';
                    obj.text =
                        "You can't open this card while currently being a cardholder of the following card(s).";
                    if (
                        this.offenders.hasOwnProperty('cardholder_another_card')
                    ) {
                        this.offenders['cardholder_another_card'].forEach(
                            (item) => {
                                obj.text +=
                                    '<div class="text-sm text-slate-500 dark:text-slate-400 block">(' +
                                    item.card_title +
                                    (item.last_four
                                        ? ' <span class="text-xs">- ' +
                                          item.last_four +
                                          '</span>'
                                        : '') +
                                    ')</div>';
                            }
                        );
                    }
                    obj.offenders = [];
                    break;
                case 'current_card_holder_family':
                    obj.title =
                        'Current ' +
                        (this.card?.family?.name || '') +
                        ' Family Cardholder';
                    obj.type = '';
                    obj.text =
                        "You can't open a new " +
                        (this.card?.family?.name || '') +
                        ' product while currently holding another card in the same product family.<br>';
                    if (
                        this.offenders.hasOwnProperty(
                            'current_card_holder_family'
                        )
                    ) {
                        this.offenders['current_card_holder_family'].forEach(
                            (item) => {
                                obj.text +=
                                    '<span class="text-sm text-slate-500 dark:text-slate-400 block">(' +
                                    item.card_title +
                                    (item.last_four
                                        ? ' <span class="text-xs">- ' +
                                          item.last_four +
                                          '</span>'
                                        : '') +
                                    ')</span>';
                            }
                        );
                    }
                    obj.offenders = [];
                    break;
                case 'time_since_bonus':
                    options = this.getCardRuleOptions('time_since_bonus');
                    let months = 0;
                    (options[0] || []).forEach((opt) => {
                        if (opt.key === 'time_since_bonus') months = opt.value;
                    });
                    obj.type = '';
                    if (months > 48) {
                        obj.title = 'Lifetime Bonus Restrictions';
                        obj.hasTooltip = false;
                        obj.text =
                            "You're likely ineligible to receive a bonus on a new " +
                            (this.card?.title || 'card') +
                            ' since you have received a bonus on it.';
                    } else {
                        obj.title = months + ' Months Since Bonus';
                        obj.hasTooltip = true;
                        obj.text =
                            "You're likely ineligible to receive a bonus on a new " +
                            (this.card?.title || 'card') +
                            ' since you received a bonus on it in the last ' +
                            months +
                            ' months.<br><span class="text-sm text-slate-500 dark:text-slate-400 block">(You\'ll be eligible again after approximately ';
                        if (this.offenders.hasOwnProperty('time_since_bonus')) {
                            obj.text += this.offenders['time_since_bonus']
                                .map((mess) => mess.fall_off_date)
                                .join(', ');
                        }
                        obj.text += ')</span>';
                    }
                    obj.offenders = [];
                    break;
                case 'time_since_closed':
                    options = this.getCardRuleOptions('time_since_closed');
                    obj.title = 'Time Since Closed on Another Card';
                    obj.type = '';
                    obj.text =
                        "You're likely ineligible to receive a bonus on a new " +
                        (this.card?.title || 'card');
                    obj.offenders = [];
                    break;
                case 'time_since_bonus_another_card':
                    options = this.getCardRuleOptions(
                        'time_since_bonus_another_card'
                    );
                    obj.title = 'Time Since Bonus on Another Card';
                    obj.type = '';
                    obj.text =
                        "You're likely ineligible to receive a bonus on a new " +
                        (this.card?.title || 'card') +
                        ' Product since you received a bonus for the following:<br>';
                    obj.hasTooltip = true;
                    if (
                        this.offenders.hasOwnProperty(
                            'time_since_bonus_another_card'
                        )
                    ) {
                        this.offenders['time_since_bonus_another_card'].forEach(
                            (item) => {
                                obj.text +=
                                    '<span class="text-sm text-slate-500 dark:text-slate-400 block">(' +
                                    item.card_title +
                                    ' <span class="text-xs">' +
                                    (item.last_four
                                        ? item.last_four + ' - '
                                        : '') +
                                    item.bonus_date +
                                    '</span>)</span>';
                            }
                        );
                    }
                    obj.offenders = [];
                    break;
                case 'have_or_have_had_in_time_period':
                    options = this.getCardRuleOptions(
                        'have_or_have_had_in_time_period'
                    );
                    obj.title = 'Have or Have Had in Time Period';
                    obj.type = '';
                    obj.text =
                        "You're likely ineligible to receive a bonus on a new " +
                        (this.card?.title || 'card') +
                        ' Product since you received a bonus for the following: ';
                    obj.offenders = [];
                    break;
                case 'have_opened_recently':
                    options = this.getCardRuleOptions('have_opened_recently');
                    obj.title = 'Other Card Open Recently ';
                    obj.type = '';
                    obj.text =
                        "You're likely ineligible to receive a bonus on a new " +
                        (this.card?.title || 'card') +
                        ' Product since you opened the following:<br>';
                    obj.hasTooltip = true;
                    if (this.offenders.hasOwnProperty('have_opened_recently')) {
                        this.offenders['have_opened_recently'].forEach(
                            (item) => {
                                obj.text +=
                                    '<span class="text-sm text-slate-500 dark:text-slate-400 block">(' +
                                    item.card_title +
                                    ' <span class="text-xs">' +
                                    (item.last_four
                                        ? item.last_four + ' - '
                                        : '') +
                                    item.date_opened +
                                    '</span>)</span>';
                            }
                        );
                    }
                    obj.offenders = [];
                    break;
                case 'amex_bonus_once_lifetime':
                    obj.title = 'AMEX Bonus Lifetime';
                    obj.type = 'Exclusion';
                    obj.text = 'Eligible again on: ';
                    obj.hasTooltip = true;
                    if (
                        this.offenders.hasOwnProperty(
                            'amex_bonus_once_lifetime'
                        )
                    ) {
                        this.offenders['amex_bonus_once_lifetime'].forEach(
                            (mess) => {
                                obj.text += mess.next_eligible_date;
                            }
                        );
                    }
                    obj.offenders = [];
                    break;
                default:
                    obj.title = 'Generic';
                    obj.type = 'Exclusion';
                    obj.text = 'Generic';
                    obj.offenders = [];
                    obj.hasTooltip = false;
            }
            return obj;
        },
        getCardRuleOptions(ruleName) {
            try {
                const rules = JSON.parse(this.card?.next_card_rules || '[]');
                const cardRules = rules.find((rule) => rule.name === ruleName);
                if (!cardRules) return [];
                return cardRules.options ? cardRules.options : [];
            } catch (e) {
                return [];
            }
        },
    },
    mounted() {
        // Esc to close
        this._escHandler = (e) => {
            if (e.key === 'Escape' && this.isOpen) this.emitClose();
        };
        window.addEventListener('keydown', this._escHandler);
    },
    beforeUnmount() {
        if (this._escHandler)
            window.removeEventListener('keydown', this._escHandler);
    },
};
</script>
<style scoped>
/* Ajuste tipográfico a 14px/20px para los textos de ExclusionText dentro del modal */
.modal-exclusion :deep(p) {
    font-size: 14px;
    line-height: 20px;
    color: #1e293b;
    margin-bottom: 0;
}
</style>
